import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import ptTranslations from '../locales/pt.json';
import enTranslations from '../locales/en.json';
import frTranslations from '../locales/fr.json';
import esTranslations from '../locales/es.json';
import zhTranslations from '../locales/zh.json';

const LanguageContext = createContext();

const translations = {
  pt: ptTranslations,
  en: enTranslations,
  fr: frTranslations,
  es: esTranslations,
  zh: zhTranslations,
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('conexao-saude-language');
    return saved || 'pt';
  });

  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];

    for (const k of keys) {
      value = value?.[k];
    }

    if (value === undefined || value === null) {
      // Fallback para português se não encontrar no idioma atual
      let fallbackValue = translations['pt'];
      for (const k of keys) {
        fallbackValue = fallbackValue?.[k];
      }
      if (fallbackValue !== undefined && fallbackValue !== null) {
        value = fallbackValue;
      } else {
        return key; // Retorna a chave se não encontrar em nenhum idioma
      }
    }

    if (typeof value === 'string') {
      let result = value;
      for (const [paramKey, paramValue] of Object.entries(params)) {
        result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
      }
      return result;
    }

    return value; // Retorna o valor original (array, objeto, etc.)
  }, [currentLanguage]);


  const changeLanguage = (languageCode) => {
    if (translations[languageCode]) {
      setCurrentLanguage(languageCode);
      localStorage.setItem('conexao-saude-language', languageCode);
    }
  };

  const supportedLanguages = [
    { code: 'pt', name: 'Português', flag: '🇬🇼', nativeName: 'Português' },
    { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
    { code: 'fr', name: 'Français', flag: '🇫🇷', nativeName: 'Français' },
    { code: 'es', name: 'Español', flag: '🇪🇸', nativeName: 'Español' },
    { code: 'zh', name: '中文', flag: '🇨🇳', nativeName: '中文' },
  ];

  return (
    <LanguageContext.Provider value={{
      t,
      currentLanguage,
      changeLanguage,
      supportedLanguages,
      translations: translations[currentLanguage]
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
